import React, { useState } from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, InfoCard, GradientButton, CTABanner } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { EXPLORE_HUB } from '../../lib/mockExplore';
import { Check } from 'lucide-react';

export default function ExploreWebLearningHub() {
  const d = EXPLORE_HUB;
  const [chosen, setChosen] = useState(null);

  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="explore-hub-hero">
        <div className="mx-auto max-w-[1240px]">
          <Eyebrow color="purple">{d.hero.eyebrow}</Eyebrow>
          <div className="mt-3 grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <h1 className="text-[40px] md:text-[52px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton to="/explore/access-request" data-testid="explore-hub-cta-primary">Request Access</GradientButton>
                <GradientButton to="/" variant="outline" data-testid="explore-hub-cta-secondary">Choose Your Role</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl border border-canvas-200 bg-gradient-to-br from-purple-50 via-white to-mint-50 p-4 md:p-6 shadow-soft">
              {/* Laptop mockup with web learning experience image */}
              <div className="relative">
                <div className="rounded-t-xl bg-navy p-1.5 shadow-card ring-1 ring-navy/30">
                  <div className="flex items-center gap-1.5 px-1 py-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-400"></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400"></span>
                    <span className="h-1.5 w-1.5 rounded-full bg-mint-500"></span>
                  </div>
                  <div className="overflow-hidden rounded-lg bg-white">
                    <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_wix-redesign-2/artifacts/43lild4i_Gyan%20Dhara%20web%20learning%20experience.png" alt="Gyan Dhara web learning experience" className="block w-full h-auto" loading="lazy" />
                  </div>
                </div>
                <div className="mx-auto h-2 w-[92%] rounded-b-xl bg-gradient-to-b from-canvas-200 to-canvas-100 shadow-inner" />
                <div className="mx-auto mt-0.5 h-1 w-[45%] rounded-full bg-canvas-200/70" />
              </div>
              <div className="mt-4">
                <div className="text-[10.5px] font-bold tracking-widest text-purple">{d.hero.dashboard.k}</div>
                <div className="mt-1 text-[16px] font-bold text-royal">{d.hero.dashboard.v}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONNECTED LEARNING TOOLS */}
      <section className="px-6 py-16" data-testid="explore-hub-connected">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.connected.eyebrow} title={d.connected.title} desc={d.connected.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {d.connected.items.map((c, i) => (<InfoCard key={i} letter={c.letter} color={c.color} title={c.title} desc={c.desc} />))}
          </div>
        </div>
      </section>

      {/* QUIZ PATHWAYS */}
      <section className="px-6 py-16 bg-white" data-testid="explore-hub-practice">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.practice.eyebrow} title={d.practice.title} desc={d.practice.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {d.practice.types.map((t, i) => (<InfoCard key={i} letter={t.letter} color={t.color} title={t.title} desc={t.desc} />))}
          </div>

          <div className="mt-10 rounded-3xl border border-canvas-200 bg-canvas-100 p-6 md:p-8 shadow-soft">
            <div className="text-[11.5px] font-bold tracking-widest text-purple">SAMPLE QUESTION</div>
            <div className="mt-1 text-[17px] font-bold text-royal">{d.practice.demo.title}</div>
            <p className="mt-3 text-[14.5px] text-navy leading-relaxed max-w-3xl">{d.practice.demo.question}</p>
            <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
              {d.practice.demo.options.map(o => {
                const active = chosen === o.k;
                const showCorrect = chosen && o.correct;
                const showWrong = active && !o.correct;
                return (
                  <button key={o.k} onClick={() => setChosen(o.k)} className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${showCorrect ? 'border-mint-500 bg-mint-50' : showWrong ? 'border-red-300 bg-red-50' : 'border-canvas-200 bg-white hover:border-purple'}`}>
                    <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg text-[13px] font-bold ${showCorrect ? 'bg-mint text-white' : showWrong ? 'bg-red-500 text-white' : 'bg-purple-50 text-purple'}`}>{o.k}</span>
                    <span className="text-[13.5px] font-semibold text-navy">{o.v}</span>
                  </button>
                );
              })}
            </div>
            {chosen && (
              <div className="mt-5 rounded-xl bg-white p-4 border border-canvas-200">
                <div className="text-[13px] font-bold text-navy">{d.practice.demo.afterTitle}</div>
                <ul className="mt-2 space-y-1">
                  {d.practice.demo.afterLines.map((l, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" /> {l}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PROGRESS AT A GLANCE */}
      <section className="px-6 py-16" data-testid="explore-hub-progress">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.progress.eyebrow} title={d.progress.title} desc={d.progress.desc} />
          <div className="mt-8 rounded-3xl border border-canvas-200 bg-white p-6 md:p-8 shadow-soft">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.35fr_1fr]">
              <div>
                <div className="text-[13px] font-bold text-navy">{d.progress.overviewTitle}</div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {d.progress.metrics.map((m, i) => (
                    <div key={i} className="rounded-xl bg-canvas-100 p-4">
                      <div className="text-[10.5px] font-bold tracking-widest text-purple">{m.label}</div>
                      <div className="mt-2 text-[22px] font-extrabold text-navy">{m.k}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <div className="text-[12.5px] font-bold text-navy">Performance by learning type</div>
                  <div className="mt-3 space-y-3">
                    {d.progress.performance.map((p, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-[12.5px]"><span className="font-semibold text-ink-700">{p.label}</span><span className="font-bold text-navy">{p.v}%</span></div>
                        <div className="mt-1 h-2 rounded-full bg-canvas-200 overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${i === 0 ? 'from-purple to-royal' : i === 1 ? 'from-royal to-cyan' : 'from-cyan to-mint'}`} style={{ width: `${p.v}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[13px] font-bold text-navy">Recent learning</div>
                <ul className="mt-4 space-y-2">
                  {d.progress.recent.map((r, i) => (
                    <li key={i} className="flex items-center justify-between rounded-lg border border-canvas-200 bg-white px-3 py-2.5">
                      <span className="text-[13px] font-semibold text-navy">{r.title}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-bold tracking-wide ${r.status === 'Completed' ? 'bg-mint-50 text-mint-600' : r.status === 'In progress' ? 'bg-royal-50 text-royal-600' : 'bg-purple-50 text-purple'}`}>{r.status.toUpperCase()}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-lg bg-canvas-100 p-3 text-[12px] text-ink-500">Time spent, module completion and quiz scores update after each session.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24" data-testid="explore-hub-cta">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} desc={d.cta.desc} primary={d.cta.primary} secondary={d.cta.secondary} />
        </div>
      </section>
    </PageShell>
  );
}
